import React from "react";
import * as Router from "next/router";
import styled from "styled-components";
import * as u from "../../utils";
import * as L from "../../components/Layout";
import * as UserContext from "../../components/UserContext";
import * as T from "../../components/Typography";
import CONTENT_TAGS from "../../utils/content-tags";
import RxSubNavEvents from "../../components/ui/navigation/RxSubNavEvents";
import EventCollection from "../../components/EventCollection";
import EventCard from "../../components/EventCard";
import EventVideoCard from "../../components/EventVideoCard";
import PromoBanner from "../../components/PromoBanner";
import Head from "next/head";
import DropdownFilter from "../../components/DropdownFilter";
import { VerticalDivider } from "../../components/Utils";
import Search from "../../components/Search";
import { apiEventGetAll } from "../api/event/get-all";

export default function Events(props) {
  const userInfo = React.useContext(UserContext.Context);
  const isLoggedIn = !!userInfo.id;
  const router = Router.useRouter();
  const canFilter = isLoggedIn && !userInfo.isClub;
  const [showAccessEvents, setShowAccessEvents] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState();
  const [selectedTags, setSelectedTags] = React.useState([]);

  React.useEffect(updateUrl, [searchValue, selectedTags]);
  React.useEffect(() => {
    const tags = JSON.parse(router.query.tags || "[]");
    setSelectedTags(tags);
  }, [router.query.tags]);

  return (
    <>
      <Head>
        <title>{`Our Events | AllBright`}</title>
      </Head>
      <RxSubNavEvents />
      <L.Container>
        <S.SearchAndFiltersContainer>
          <S.Desktop>
            <VerticalDivider />
          </S.Desktop>
          <DropdownFilter
            name="events-category"
            hint="Filter events by category"
            list={CONTENT_TAGS}
            value={selectedTags}
            onSelect={setSelectedTags}
          />
          <S.Desktop>
            <VerticalDivider />
          </S.Desktop>
          <Search
            initialValue={router.query.search}
            onSearch={(searchValue) => setSearchValue(searchValue)}
            name="events"
          />
        </S.SearchAndFiltersContainer>

        <PromoBanner />
        <EventCollection
          heading={"Upcoming events"}
          datacy="upcomingEvents"
          canFilter={canFilter}
          showAccessEvents={showAccessEvents}
          setShowAccessEvents={setShowAccessEvents}
        >
          {getUpcomingEvents(props.events).map(getEvent).filter(Boolean)}
        </EventCollection>

        {getRecordedEvents(props.events).length !== 0 && (
          <EventCollection
            heading={"Watch event videos"}
            datacy="recordedEvents"
          >
            {getRecordedEvents(props.events).map(getRecordedEvent)}
          </EventCollection>
        )}

        {getPastEvents(props.events).length !== 0 && (
          <EventCollection heading={"Past events"} datacy="pastEvents">
            {getPastEvents(props.events).map(getEvent).filter(Boolean)}
          </EventCollection>
        )}

        {getUpcomingEvents(props.events).length === 0 &&
          getRecordedEvents(props.events).length === 0 &&
          getPastEvents(props.events).length === 0 && (
            <T.CL>No matching events found</T.CL>
          )}
      </L.Container>
    </>
  );

  function updateUrl() {
    if (searchValue === undefined && selectedTags.length === 0) return;

    let queryStringParams = new URLSearchParams(router.query);

    if (searchValue?.length) {
      queryStringParams.set("search", searchValue);
    } else {
      queryStringParams.delete("search");
    }
    if (selectedTags?.length) {
      queryStringParams.set("tags", JSON.stringify(selectedTags));
    } else {
      queryStringParams.delete("tags");
    }

    return router.replace(`/events?${queryStringParams.toString()}`);
  }

  function getUpcomingEvents(events) {
    const isFutureEvent = (e) => e.startTime >= new Date().getTime() / 1000;
    const oldestFirst = (a, b) => a.startTime - b.startTime;

    let copy = [...events];
    copy = copy.filter(isFutureEvent).sort(oldestFirst);
    if (searchValue) copy = copy.filter(isSearchTerm);
    if (selectedTags.length > 0)
      copy = copy.filter((event) =>
        event.tags?.some((tag) => selectedTags.includes(tag))
      );
    if (showAccessEvents) copy = copy.filter(isAvailableToMe);

    return copy;
  }

  function getEvent(props, index) {
    const isExclusive = !!props.exclusiveGroups?.length;
    const userHasAccess = props.exclusiveGroups?.some((group) =>
      userInfo.groups?.includes(group)
    );
    if (isExclusive && !userHasAccess) return null;

    const showLock = !isAvailableToMe(props);

    const subProps = {
      key: index,
      index: index,
      ...props,
    };

    return <EventCard showLock={showLock} {...subProps} />;
  }

  function getRecordedEvents(events) {
    if (showAccessEvents && !userInfo.isPaid) return [];

    const hasRecording = (e) => !!e.recordedEventVideoUrl;
    const newestFirst = (a, b) => b.startTime - a.startTime;

    let copy = [...events];
    copy = copy.filter(hasRecording);
    if (searchValue) copy = copy.filter(isSearchTerm);
    if (selectedTags.length > 0)
      copy = copy.filter((event) =>
        event.tags?.some((tag) => selectedTags.includes(tag))
      );
    copy = copy.sort(newestFirst);
    return copy;
  }

  function getRecordedEvent(props, index) {
    const showLock = !isAvailableToMe(props);

    const subProps = {
      key: index,
      index: index,
      initialListSize: 4,
      ...props,
    };

    return <EventVideoCard showLock={showLock} {...subProps} />;
  }

  function getPastEvents(events) {
    const isPastEvent = (e) => e.startTime < new Date().getTime() / 1000;
    const newestFirst = (a, b) => b.startTime - a.startTime;

    let copy = [...events];
    copy = copy.filter(isPastEvent);
    if (showAccessEvents) copy = copy.filter(isAvailableToMe);
    if (searchValue) copy = copy.filter(isSearchTerm);
    if (selectedTags.length > 0)
      copy = copy.filter((event) =>
        event.tags?.some((tag) => selectedTags.includes(tag))
      );
    copy = copy.sort(newestFirst);

    return copy;
  }

  function isSearchTerm(event) {
    const title = event.title.toLowerCase();

    return title.includes(searchValue.toLowerCase());
  }

  function isAvailableToMe(props) {
    if (!isLoggedIn || props.isFree) return true;

    switch (props.memberAccess) {
      case "REGISTERED":
        return isLoggedIn;
      case "CLUB":
        return !!userInfo.isClub;
      default:
        return !!userInfo.isPaid;
    }
  }
}

export async function getStaticProps() {
  const events = await apiEventGetAll();
  return { props: { events }, revalidate: 3600 };
}

const S = {};

S.SearchAndFiltersContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

S.Desktop = styled.div`
  display: none;

  @media ${u.device.tablet} {
    display: flex;
  }
`;
